/**
 * ParticleSystem.ts — CPU-side particle pool for bow spray + turbulent wake.
 *
 * Fixed-capacity ring buffer. Per particle: world position, velocity, age,
 * lifetime, size, kind (0 = spray, 1 = wake-bubble) packed for GPU upload.
 *
 * Spawning is driven externally (e.g. BoatSpawner) — this class only owns
 * storage + integration + GPU buffer write.
 *
 * Layout per particle on GPU (32 bytes):
 *   pos.xyz, age, vel.xyz, lifeRecip, kind, sizeStart, sizeEnd, _pad
 *
 * Wait — that is 9 floats; pad to 32 B as 8 floats: pos.xyz, age, vel.xyz,
 * packed (lifeRecip × 1e3 + kind in fractional), where kind ∈ {0,1}. Cleaner
 * to use 12 floats = 48 B aligned.
 */

const FLOATS_PER_PARTICLE = 12;
export const PARTICLE_STRIDE_BYTES = FLOATS_PER_PARTICLE * 4;

export const PARTICLE_KIND_SPRAY = 0;
export const PARTICLE_KIND_WAKE = 1;
/** Horizontal wind-streak particles — no gravity, no drag. */
export const PARTICLE_KIND_WIND = 2;

export class ParticleSystem {
  readonly capacity: number;
  /** Index of next-to-overwrite slot (round-robin). */
  private writeIdx = 0;
  /** Number of currently alive particles (for draw count cap). */
  aliveCount = 0;

  // Parallel arrays (faster than struct-of-pointers).
  private px: Float32Array;
  private py: Float32Array;
  private pz: Float32Array;
  private vx: Float32Array;
  private vy: Float32Array;
  private vz: Float32Array;
  private age: Float32Array;
  private life: Float32Array;
  private sizeStart: Float32Array;
  private sizeEnd: Float32Array;
  private kind: Uint8Array;

  /** Interleaved CPU staging for GPU upload. */
  readonly cpuBuffer: ArrayBuffer;
  private cpuView: Float32Array;

  constructor(capacity: number) {
    this.capacity = capacity;
    this.px = new Float32Array(capacity);
    this.py = new Float32Array(capacity);
    this.pz = new Float32Array(capacity);
    this.vx = new Float32Array(capacity);
    this.vy = new Float32Array(capacity);
    this.vz = new Float32Array(capacity);
    this.age = new Float32Array(capacity);
    this.life = new Float32Array(capacity);
    this.sizeStart = new Float32Array(capacity);
    this.sizeEnd = new Float32Array(capacity);
    this.kind = new Uint8Array(capacity);
    this.cpuBuffer = new ArrayBuffer(capacity * PARTICLE_STRIDE_BYTES);
    this.cpuView = new Float32Array(this.cpuBuffer);
    // Initialise life=0 so all slots count as dead.
  }

  spawn(
    x: number,
    y: number,
    z: number,
    vx: number,
    vy: number,
    vz: number,
    life: number,
    sizeStart: number,
    sizeEnd: number,
    kind: number,
  ): void {
    const i = this.writeIdx;
    this.writeIdx = (i + 1) % this.capacity;
    this.px[i] = x;
    this.py[i] = y;
    this.pz[i] = z;
    this.vx[i] = vx;
    this.vy[i] = vy;
    this.vz[i] = vz;
    this.age[i] = 0;
    this.life[i] = life;
    this.sizeStart[i] = sizeStart;
    this.sizeEnd[i] = sizeEnd;
    this.kind[i] = kind;
  }

  /** Integrate one step. Gravity in m/s². Air drag is 1/τ. */
  step(dt: number, gravity: number, dragTau: number): void {
    const dragK = 1 - Math.exp(-dt / dragTau);
    let alive = 0;
    for (let i = 0; i < this.capacity; i++) {
      if (this.life[i]! <= 0) continue;
      const a = this.age[i]! + dt;
      if (a >= this.life[i]!) {
        this.life[i] = 0;
        continue;
      }
      this.age[i] = a;
      if (this.kind[i] === PARTICLE_KIND_WIND) {
        // Wind streaks: no forces.
      } else if (this.kind[i] === PARTICLE_KIND_WAKE) {
        // Foam on the water surface: no gravity (it floats), strong horizontal
        // drag so the patch settles in place after spawn. Without this the
        // wake bubble fell underwater within ~0.3 s and the trail vanished.
        const wakeDrag = 1 - Math.exp(-dt / 0.25);
        this.vx[i]! -= this.vx[i]! * wakeDrag;
        this.vz[i]! -= this.vz[i]! * wakeDrag;
        this.vy[i] = 0;
      } else {
        // Spray: drag + gravity.
        this.vx[i]! -= this.vx[i]! * dragK;
        this.vy[i]! -= this.vy[i]! * dragK;
        this.vz[i]! -= this.vz[i]! * dragK;
        this.vy[i]! -= gravity * dt;
      }
      // Integrate.
      this.px[i]! += this.vx[i]! * dt;
      this.py[i]! += this.vy[i]! * dt;
      this.pz[i]! += this.vz[i]! * dt;
      alive++;
    }
    this.aliveCount = alive;
  }

  /** Pack alive particles into the front of cpuBuffer. Returns count packed. */
  packAlive(): number {
    const v = this.cpuView;
    let n = 0;
    for (let i = 0; i < this.capacity; i++) {
      if (this.life[i]! <= 0) continue;
      const o = n * FLOATS_PER_PARTICLE;
      v[o + 0] = this.px[i]!;
      v[o + 1] = this.py[i]!;
      v[o + 2] = this.pz[i]!;
      v[o + 3] = this.age[i]!;
      v[o + 4] = this.vx[i]!;
      v[o + 5] = this.vy[i]!;
      v[o + 6] = this.vz[i]!;
      v[o + 7] = this.life[i]!;
      v[o + 8] = this.sizeStart[i]!;
      v[o + 9] = this.sizeEnd[i]!;
      v[o + 10] = this.kind[i]!;
      v[o + 11] = 0; // pad
      n++;
    }
    return n;
  }
}
